<template>
    <div>
        <hts-drawer
            v-model="visible"
            color="main"
            :title="$t('labels.documentList')"
            size="large"
            @close="visible = false">
            <div class="mb-3 text-right">
                <hts-form-button
                    v-if="checkPermission(layoutPermissions.driver_document_add)"
                    color="main"
                    icon-name="hi-plus-circle-icon"
                    icon-position="right"
                    type="submit"
                    @click="openAddDocumentDriver">
                    {{ $t('labels.addDocument') }}
                </hts-form-button>
            </div>

            <hts-data-table
                ref="dataDocument"
                :headers="documentDriver"
                :server="server"
                :detail-row-auto-height="true"
                :use-archive="useArchive"
                :use-restore="useRestore"
                use-selection
                store-name="driverDataTable"
                store-key="DriverDocument" />
            <hts-modal
                v-if="checkPermission(layoutPermissions.driver_document_delete)"
                :show="showDelete"
                :title="$t('labels.deleteDocument')"
                type="confirm"
                :content="$t('labels.deleteDocumentContent')"
                @close="closePopupDeleteDriverDocument"
                @confirm="() => actionDeleteDriverDocument()" />
        </hts-drawer>

        <add-document
            v-if="visible"
            ref="addDocumentDriver"
            :code-driver="idDriver"
            :show="showAdd"
            @refresh-tables="refreshTables()"
            @close="showAdd = false" />

        <edit-document
            v-if="visible"
            ref="editDocumentDriver"
            :show="showEdit && checkPermission(layoutPermissions.driver_document_edit)"
            :document-info="documentSelected"
            :code-driver="idDriver"
            @refreshTables="refreshTables()" />
    </div>
</template>

<script lang="ts" setup>
import AddDocument from './add-document.vue'
import EditDocument from './edit-document-driver.vue'
import layoutPermissions from '@/data/permissions/layout-permissions'
import { checkPermission } from '@/utils/permission-check'
import Actions from '~/pages/trucker-[id]/vehicle/ignore/actions.vue'
const { $formatters } = useNuxtApp()

const props = defineProps({
  color: {
    type: String,
    default: 'theme'
  },
  sizeSlideOver: {
    type: String,
    default: 'screen-lg'
  },
  column: {
    type: Number,
    default: 3
  }
})

const server = ref({})
const visible = ref(false)
const addDocumentDriver = ref()
const editDocumentDriver = ref()
const idDriver = ref('')
const open = (idDriverSelected: string) => {
  visible.value = true
  server.value = {
    method: 'post',
    url: '/driver/document/list/' + idDriverSelected
  }
  idDriver.value = idDriverSelected
}
defineExpose({
  open
})

const emits = defineEmits(['close', 'update:show'])

const showDelete = ref(false)
const dataDocument = ref()
const showAdd = ref(false)
const showEdit = ref(false)
const documentSelected = ref({} as { [key: string]: any })

const handleDelete = (document: any) => {
  showDelete.value = true
  documentSelected.value = document
}

const handleEdit = (document: any) => {
  if (editDocumentDriver.value) {
    editDocumentDriver.value.open(document)
  }
}
const openAddDocumentDriver = () => {
  if (addDocumentDriver.value) {
    addDocumentDriver.value.open()
  }
}

const documentDriver = [
  {
    field: 'type',
    headerName: $t('labels.documentType'),
    sortable: true,
    valueFormatter: (params: any) => {
      if (params.data.type === 'HEALTHCHECK') {
        return $t('labels.healthCheck')
      } else if (params.data.type === 'INSURANCE') {
        return $t('labels.insurance')
      } else if (params.data.type === 'CONTRACT') {
        return $t('labels.contract')
      } else if (params.data.type === 'OTHER') {
        return $t('labels.other')
      }
    }
  },
  {
    field: 'fileName',
    headerName: $t('labels.documentFileName'),
    sortable: true,
    filter: 'agTextColumnFilter'
  },
  {
    field: 'path',
    headerName: $t('labels.link'),
    maxWidth: 250,
    sortable: true,
    valueFormatter: (params: any) => {
      return $formatters.cdnUrl(params.data)
    },
    onCellClicked: (params: any) => {
      window.open($formatters.cdnUrl(params.data))
    }
  },
  {
    field: 'healthResult',
    headerName: $t('labels.driverModule.healthCheck.value'),
    sortable: true,
    valueGetter: (params: any) => {
      if (params.data.type === 'HEALTHCHECK') {
        if (Number(params.data.healthResult) === 0) {
          return $t('labels.failed')
        } else if (Number(params.data.healthResult) === 1) {
          return $t('labels.passed')
        }
      }
      return ''
    }
  },
  {
    field: 'expiredDate',
    headerName: $t('labels.expiredDate'),
    sortable: true,
    valueGetter: (params: any) => {
      if (!params.data.expiredDate) {
        return ''
      }

      if (params.data.type === 'HEALTHCHECK' || params.data.type === 'CONTRACT') {
        return $formatters.date(params.data.expiredDate)
      }
    }
  },
  {
    field: 'description',
    headerName: $t('labels.description'),
    sortable: true
  },
  {
    field: 'createdAt',
    headerName: $t('labels.createdAt'),
    sortable: true,
    valueGetter: (params: any) => {
      return $formatters.datetime(params.data.createdAt)
    }
  },
  {
    field: 'action',
    pinned: 'right',
    sortable: false,
    lockPosition: true,
    lockPinned: true,
    headerName: $t('labels.action'),
    cellStyle: { overflow: 'visible' },
    cellRenderer: Actions,
    cellRendererParams: {
      storeKey: 'driverDataTable',
      storeName: 'DriverDocument',
      items: [
        {
          icon: 'hi-pencil-icon',
          label: $t('labels.edit'),
          class: 'text-main-600 hover:text-main-500',
          permissions: checkPermission(layoutPermissions.driver_document_edit),
          click: handleEdit
        },
        {
          icon: 'hi-trash-icon',
          label: $t('labels.delete'),
          class: 'text-error-500',
          permissions: checkPermission(layoutPermissions.driver_document_delete),
          click: handleDelete,
          isArchive: true
        }
      ]
    }
  }
]

const refreshTables = () => {
  if (dataDocument.value) {
    dataDocument.value.refresh()
  }
}

const closePopupDeleteDriverDocument = () => {
  showDelete.value = false
  Object.assign(documentSelected, {})
}

const actionDeleteDriverDocument = () => {
  const api = {
    url: '/driver/document/delete',
    method: 'DELETE'
  }
  $api(api, { body: { ids: [documentSelected.value?._id] } }).then((data) => {
    const resData: any = data?.data?.value

    if (resData?.success) {
      Object.assign(documentSelected, {})
      refreshTables()
      showDelete.value = false
      process.client && $toast().success($t('notifications.deleteSuccessDocument'))
    }
  })
}

const useArchive = { api: $url.driver.document.archive, permissions: checkPermission(layoutPermissions.driver_document_archive) }
const useRestore = { api: $url.driver.document.restore, permissions: checkPermission(layoutPermissions.driver_document_restore) }
</script>
